import { useTranslation } from "react-i18next";
import { Button, Form, Layout, message, Modal, Tabs } from "antd";
import { TabsProps } from "antd/lib";
import { useEffect, useState } from "react";
import PageHeader from "../../components/page-header/PageHeader";
import PrivacyPolicy from "./privacy-policy/PrivacyPolicy";
import TermsOfService from "./terms-of-service/TermsOfService";
import { addMoocPolicy, moocPolicyGetAllByFilter } from "../../service/clause";
import { globalStore } from "../../stores/stores";
import { useNavigate } from "react-router-dom";
import { routesConfig } from "../../config/routes";
import "./clause.scss";

type ClauseProps = {
  title?: string;
};

const Clause = ({ title }: ClauseProps) => {
  const { t } = useTranslation();
  const [keyActive, setKeyActive] = useState("1");
  const [allData, setAllData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { reload } = globalStore();
  const [form] = Form.useForm();

  const listBreadcrumb = [
    {
      title: t("Bảo mật và điều khoản", "Bảo mật và điều khoản"),
    },
  ];

  const itemsTab: TabsProps["items"] = [
    {
      key: "1",
      label: t("Chính sách bảo mật", "Chính sách bảo mật"),
      children: (
        <PrivacyPolicy form={form} loading={loading} allData={allData} />
      ),
    },
    {
      key: "2",
      label: t("Điều khoản dịch vụ", "Điều khoản dịch vụ"),
      children: (
        <TermsOfService form={form} loading={loading} allData={allData} />
      ),
    },
  ];

  // Tabs
  const onTabClick = (key: string) => {
    setKeyActive(key);
  };

  // handle publish
  const handlePublish = () => {
    return Modal.confirm({
      title: `${t("Xuất bản", "Xuất bản")} ${
        keyActive === "1" ? t("Điều khoản dịch vụ", "Điều khoản dịch vụ") : t("Chính sách bảo mật", "Chính sách bảo mật")
      }`,
      content: t("Bạn có chắc chắn muốn xuất bản?", "Bạn có chắc chắn muốn xuất bản?"),
      onOk: () => form.submit(),
      centered: true,
      okText: t("Xác nhận", "Xác nhận"),
      cancelText: t("Huỷ bỏ", "Huỷ bỏ"),
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <OkBtn />
        </>
      ),
    });
  };

  // fetch data
  const fetchData = async () => {
    setLoading(true);
    await moocPolicyGetAllByFilter({
      type: Number(keyActive),
      sort: ["order_number, asc"],
      enterpriseId: localStorage.getItem("schoolManagement"),
    })
      .then((res) => {
        if (res.status === 200) {
          setAllData(res.data.data.content);
          setTimeout(() => {
            setLoading(false);
          }, 333);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // add clause
  const publishClause = async (values: any) => {
    // group by name and description
    const groupedFields = new Map<
      string | null,
      {
        id: string | number | null;
        name: string;
        description: string;
      }
    >();

    Object.keys(values).forEach((key, index) => {
      const match = key.match(/^(name|description)-(.*)$/);
      if (match) {
        const [_, type, id] = match;
        const isNew = id.startsWith("new-");
        const actualId = id;

        if (!groupedFields.has(actualId)) {
          groupedFields.set(actualId, {
            id: isNew ? null : Number(actualId),
            name: "",
            description: "",
          });
        }
        groupedFields.get(actualId)![type as "name" | "description"] =
          values[key];
      }
    });

    const result = Array.from(groupedFields.values());

    const deletedIds = allData
      .filter((i1: any) => !result.some((i2: any) => i2.id === i1.id))
      .map((item: any) => item.id);

    const newListPublish = result.map((item: any, index: number) => ({
      ...item,
      orderNumber: index + 1,
    }));

    // call api
    const dataReq = {
      list: newListPublish,
      deletedIds: deletedIds,
      type: Number(keyActive),
    };

    await addMoocPolicy(dataReq).then((res) => {
      if (res.status === 200) {
        message.success(t("Xuất bản thành công!", "Xuất bản thành công!"));
        fetchData();
      }
    });
  };

  // useEffect
  useEffect(() => {
    fetchData();
  }, [keyActive, reload]);

  return (
    <Layout className="page-header-group">
      <Form.Provider
        onFormFinish={(name, { values, forms }) => {
          if (name === "publishTermsOfService") {
            const allValues = form.getFieldsValue();
            publishClause(allValues);
          }

          if (name === "publishPrivacyPolicy") {
            const allValues = form.getFieldsValue();
            publishClause(allValues);
          }
        }}
      >
        <div className={`page-header-group--layout`}>
          <div className="table-header--wrapper">
            <div className="table-header--left">
              <PageHeader title={title} listBreadcrumb={listBreadcrumb} />
            </div>
          </div>
        </div>
        <Tabs
          className="clause-tabs"
          defaultActiveKey="1"
          items={itemsTab}
          tabPosition={"top"}
          onTabClick={onTabClick}
          renderTabBar={(props, DefaultTabBar) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <DefaultTabBar {...props} style={{ flex: 1 }} />
              <div className="flex pr-2">
                <Button
                  type="primary"
                  style={{ marginRight: 10 }}
                  onClick={handlePublish}
                >
                  {t("Xuất bản", "Xuất bản")}
                </Button>
                <Button
                  type="default"
                  style={{ marginRight: 10 }}
                  onClick={() =>
                    navigate(`${routesConfig.clauseHistory}`, {
                      state: {
                        mode: "view",
                      },
                    })
                  }
                >
                  {t("Lịch sử", "Lịch sử")}
                </Button>
              </div>
            </div>
          )}
        />
      </Form.Provider>
    </Layout>
  );
};

export default Clause;
